// src/components/sops/SopUi.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
// Small shared pieces for the SOP screens. Brand: purple #5F4EC9, navy #12192D.
import React, { useEffect } from 'react';
import { X } from 'lucide-react';

const BTN = {
    primary: 'bg-[#5F4EC9] text-white hover:bg-[#4f40b0] disabled:opacity-50',
    secondary: 'bg-white text-[#12192D] border border-gray-300 hover:bg-gray-50 disabled:opacity-50',
    danger: 'bg-white text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-50',
    link: 'text-[#5F4EC9] hover:underline disabled:opacity-50 px-0',
};

export function Button({ variant = 'secondary', className = '', ...props }) {
    return (
        <button type="button" {...props}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium min-h-[40px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 ${BTN[variant]} ${className}`} />
    );
}

/** A field heading that always says whether it's Required or Optional. */
// sopweb-bdb7afcd — stacked: the Required/Optional tag on its own line (for labels sitting side by side,
// so none of them wraps the tag differently); className lets a row of fields grow the label area.
export function FieldLabel({ htmlFor, children, required, hint, stacked, className = '' }) {
    const tag = <span className={`text-xs font-normal whitespace-nowrap ${stacked ? 'block' : 'ml-1'} ${required ? 'text-[#5F4EC9]' : 'text-gray-500'}`}>{required ? 'Required' : 'Optional'}</span>;
    return (
        <div className={`mb-1 ${className}`}>
            <label htmlFor={htmlFor} className={`text-sm font-medium text-[#12192D] ${stacked ? '[text-wrap:balance]' : ''}`}>
                {children}{stacked ? null : ' '}
                {tag}
            </label>
            {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
    );
}

export const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40 focus:border-[#5F4EC9]';

export function Notice({ tone = 'info', children, onClose }) {
    const tones = {
        info: 'bg-[#5F4EC9]/5 border-[#5F4EC9]/20 text-[#12192D]',
        error: 'bg-red-50 border-red-200 text-red-800',
        warn: 'bg-amber-50 border-amber-200 text-amber-900',
        ok: 'bg-green-50 border-green-200 text-green-800',
    };
    return (
        <div role={tone === 'error' ? 'alert' : 'status'} className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${tones[tone]}`}>
            <div className="flex-1">{children}</div>
            {onClose && <button type="button" onClick={onClose} aria-label="Dismiss" className="opacity-60 hover:opacity-100"><X size={16} /></button>}
        </div>
    );
}

export function Modal({ title, onClose, children, footer, wide }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div role="dialog" aria-modal="true" aria-label={title} className={`w-full ${wide ? 'max-w-xl' : 'max-w-md'} rounded-xl bg-white shadow-xl max-h-[90vh] flex flex-col`}>
                <div className="flex items-center justify-between border-b px-5 py-3">
                    <h2 className="text-base font-semibold text-[#12192D]">{title}</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="text-gray-500 hover:text-gray-800"><X size={18} /></button>
                </div>
                {/* sopweb-bdb7afcd — min-h-0: in a short window the middle scrolls and the buttons stay on screen. */}
                <div className="min-h-0 flex-1 px-5 py-4 overflow-y-auto space-y-4">{children}</div>
                {footer && <div className="flex flex-shrink-0 flex-wrap justify-end gap-2 border-t px-5 py-3">{footer}</div>}
            </div>
        </div>
    );
}

/** Shown after a PDF opens: the 60-second link can't be reloaded later. */
// sopweb-bdb7afcd — the viewer's own download button fails after the minute, so point people at ours.
export const OPENED_NOTE = 'The SOP opened in a new tab. Its link only works for a minute — if you reload that tab later and see an "expired" message, just open it again from here. To save or print, use Download in Spatchy.';
export const downloadedNote = (fileName) => `Downloading ${fileName || 'the SOP'} — check your browser’s downloads.`;
